import axios from 'axios'
import { checkIsViewAsMode, getUser } from '@/utils/auth'
import { logout } from '@/hooks/use-logout-listener'
import { AxiosError } from '@/exceptions/axios-error.js'
import { HttpsError } from '@/exceptions/https-error.js'
import { ValidatorError } from '@/exceptions/validator-error.js'
import { captureBatchingException } from '@/utils/posthog'
import { ExceptionCategory } from '@/constants/posthog'

const APP_VERSION = '2.0'

const apiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_EVS_URL || 'https://api.zenify.vn',
})

const emailInstance = axios.create({
  baseURL:
    import.meta.env.VITE_EMAIL_URL || 'https://api.zenify.vn/emailgateway',
})

const cdnInstance = axios.create({
  baseURL: import.meta.env.VITE_CDN_URL || 'https://cdn.zenify.vn',
})

const apiZaloInstance = axios.create({
  baseURL: import.meta.env.VITE_ZALO_LOGIN_URL,
})

const successInterceptor = (response) => {
  const status = response.data.statusCode || response.data.status
  if (response.data) {
    if (response.data instanceof Blob) {
      return Promise.resolve(response.data)
    }
    if (status === 200 || status === 201) {
      return Promise.resolve(response.data)
    }
  }
  const message = response.data.message
  return Promise.reject(new HttpsError({ status, message }))
}

const errorInterceptor = (error) => {
  if (
    error.response &&
    (error.response.data.status === 403 ||
      error.response.data.statusCode === 403 ||
      error.response.status === 403)
  ) {
    const isViewAsMode = checkIsViewAsMode()
    if (isViewAsMode) {
      console.log('Spy mode detected, not logging out')
      return
    }
    logout(true)
    return
  }

  if (error.message === 'Network Error') {
    captureBatchingException('network_error', error, {
      category: ExceptionCategory.Network,
      originator: 'errorInterceptor',
    })
  }

  if (error.response && error.response.data) {
    const status =
      error.response.data.status ||
      error.response.data.statusCode ||
      error.response.status
    if (status === 422) {
      const errors = error.response.data
      return Promise.reject(new ValidatorError(errors))
    } else {
      const message = error.response.data.message
      return Promise.reject(
        new HttpsError({ status, message, data: error.response.data }),
      )
    }
  } else {
    const { code } = error
    return Promise.reject(new AxiosError({ code }))
  }
}

apiInstance.defaults.headers.common['x-app-version'] = APP_VERSION

apiInstance.interceptors.response.use(successInterceptor, errorInterceptor)
emailInstance.interceptors.response.use(successInterceptor, errorInterceptor)
cdnInstance.interceptors.response.use(successInterceptor, errorInterceptor)

export const requestApi = async ({
  endpoint,
  isPublicApi = false,
  customHeaders = {},
  ...axiosArgs
} = {}) => {
  let token
  if (!isPublicApi) {
    const user = await getUser()
    token = user?.token
  }
  const headers = {
    Authorization: token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...customHeaders,
  }

  return apiInstance(endpoint, {
    headers,
    ...axiosArgs,
  })
}

export const postWithFile = async ({
  endpoint,
  customHeaders = {},
  ...axiosArgs
} = {}) => {
  const user = await getUser()
  const headers = {
    Authorization: user?.token,
    ...customHeaders,
  }
  const formData = new FormData()
  for (const [key, value] of Object.entries(axiosArgs.data)) {
    formData.append(key, value)
  }

  return apiInstance.post(endpoint, formData, {
    headers,
    ...axiosArgs,
  })
}

export const requestCdn = async ({
  endpoint,
  isPublicApi = false,
  ...axiosArgs
}) => {
  let token
  if (!isPublicApi) {
    const user = await getUser
    token = user?.token
  }
  const headers = {
    'Content-Type': 'multipart/form-data',
    Authorization: token,
  }

  return cdnInstance(endpoint, {
    headers,
    ...axiosArgs,
  })
}

export const requestEmail = async ({
  endpoint,
  isPublicApi = false,
  ...axiosArgs
} = {}) => {
  let token
  if (!isPublicApi) {
    const user = await getUser()
    token = user?.token
  }
  const headers = {
    Authorization: token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  return emailInstance(endpoint, {
    headers,
    ...axiosArgs,
  })
}

export const requestZaloApi = async ({ endpoint, ...axiosArgs } = {}) => {
  const user = getUser()
  const token = user?.token

  const headers = {
    Authorization: token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  return apiZaloInstance(endpoint, {
    headers,
    ...axiosArgs,
  })
}
